import React, { ComponentType, createContext, useContext, useEffect } from 'react'
import { Signal } from '@preact/signals-react'
import { useSignals } from '@preact/signals-react/runtime'
import { BlockRefType } from './TaskBlock'

export type BlockStateContextType = {
    getBlockState: (key: string) => Signal<unknown>
    setBlockState: (key: string, value: unknown) => void
    getBlockListener: (key: string) => Signal<((...args: unknown[]) => unknown) | undefined>
    setBlockListener: (key: string, listener: ((...args: unknown[]) => unknown) | undefined) => void
    setMethod: (key: string, method: (...args: unknown[]) => unknown) => void
    setRef: (ref: BlockRefType) => void
}

const BlockStateContext = createContext<BlockStateContextType | undefined>(undefined)

export const BlockStateProvider: React.FC<{
    children: React.ReactNode
    value: BlockStateContextType
}> = ({ children, value }) => {
    return <BlockStateContext.Provider value={value}>{children}</BlockStateContext.Provider>
}

export const useBlockStateContext = () => {
    const context = useContext(BlockStateContext)
    if (!context) {
        throw new Error('useBlockStateContext must be used within a BlockStateProvider')
    }
    return context
}

export function withBlockContext<P extends object>(
    Component: ComponentType<P>,
    wrapperProps: {
        getIsVisible: () => boolean
        getBlockState: (key: string) => Signal<unknown>
        setBlockState: (key: string, value: unknown) => void
        getBlockListener: (key: string) => Signal<((...args: unknown[]) => unknown) | undefined>
        setBlockListener: (
            key: string,
            listener: ((...args: unknown[]) => unknown) | undefined
        ) => void
        setMethod: (key: string, method: (...args: unknown[]) => unknown) => void
        cleanup: () => void
        blockId: string
        setRef: (ref: BlockRefType) => void
    }
): ComponentType<P> {
    return (props: P) => {
        useSignals()
        useEffect(() => {
            return () => {
                wrapperProps.cleanup()
            }
        }, [])
        return (
            <BlockStateProvider
                value={{
                    getBlockState: wrapperProps.getBlockState,
                    setBlockState: wrapperProps.setBlockState,
                    getBlockListener: wrapperProps.getBlockListener,
                    setBlockListener: wrapperProps.setBlockListener,
                    setMethod: wrapperProps.setMethod,
                    setRef: wrapperProps.setRef,
                }}
            >
                {wrapperProps.getIsVisible() && (
                    <Component {...props} blockId={wrapperProps.blockId} />
                )}
            </BlockStateProvider>
        )
    }
}
